import { Fuel } from 'lucide-react';
import { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { cn, formatDateValue, formatNumber } from '@/lib/utils';

import type { TankState, TankStatus } from '../dashboard.api';

interface TankStatusPanelProps {
  tanks: TankStatus[] | undefined;
  loading: boolean;
}

const TONES: Record<TankState, { bar: string; badge: 'success' | 'warning' | 'danger'; label: string }> = {
  normal: { bar: 'bg-brand-500', badge: 'success', label: 'Normal' },
  bajo: { bar: 'bg-amber-500', badge: 'warning', label: 'Bajo' },
  critico: { bar: 'bg-rose-500', badge: 'danger', label: 'Crítico' },
};

/** Cuántos tanques se muestran directamente en la tarjeta. */
const PREVIEW_LIMIT = 4;

/** Fila de un tanque: nivel actual, producto y barra de ocupación. */
function TankRow({ tanque }: { tanque: TankStatus }) {
  const tone = TONES[tanque.estado] ?? TONES.normal;
  const porcentaje = Math.min(Math.max(tanque.porcentaje, 0), 100);

  return (
    <li>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="flex items-center gap-2 text-sm font-medium text-ink-800">
          <Fuel className="size-4 text-ink-400" aria-hidden="true" />
          {tanque.description_tank}
          <span className="text-ink-400">·</span>
          <span className="truncate text-ink-500">{tanque.producto}</span>
        </span>

        <span className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-sm font-semibold text-ink-900 tabular-nums">
            {formatNumber(tanque.nivel_actual)} gal
          </span>
          <Badge variant={tone.badge}>{tone.label}</Badge>
        </span>
      </div>

      <div className="mt-2 h-2.5 w-full overflow-hidden rounded-full bg-ink-100">
        <div
          role="progressbar"
          aria-valuenow={Math.round(porcentaje)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`Nivel del ${tanque.description_tank}`}
          style={{ width: `${porcentaje}%` }}
          className={cn('h-full rounded-full transition-all duration-500', tone.bar)}
        />
      </div>

      <div className="mt-1.5 flex flex-wrap items-center justify-between gap-2 text-[11px] text-ink-400">
        <span>
          {porcentaje.toFixed(1)}% de {formatNumber(tanque.capacidad_maxima)} gal · mínimo{' '}
          {formatNumber(tanque.capacidad_minima)} gal
        </span>

        {tanque.ultima_medicion ? (
          <span>Medición: {formatDateValue(tanque.ultima_medicion)}</span>
        ) : null}
      </div>
    </li>
  );
}

/** Nivel de cada tanque según la última medición de PTS. */
export function TankStatusPanel({ tanks, loading }: TankStatusPanelProps) {
  const [abierto, setAbierto] = useState(false);

  if (loading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 3 }).map((_, index) => (
          <Skeleton key={index} className="h-16 rounded-control" />
        ))}
      </div>
    );
  }

  const lista = tanks ?? [];

  if (lista.length === 0) {
    return (
      <p className="py-6 text-center text-sm text-ink-500">
        No hay tanques de combustible configurados.
      </p>
    );
  }

  const vistaPrevia = lista.slice(0, PREVIEW_LIMIT);

  return (
    <div className="flex h-full flex-col gap-3">
      <ul className="space-y-4">
        {vistaPrevia.map((tanque) => (
          <TankRow key={tanque.id_tank} tanque={tanque} />
        ))}
      </ul>

      {lista.length > PREVIEW_LIMIT ? (
        <Button
          variant="outline"
          size="sm"
          onClick={() => setAbierto(true)}
          className="self-start"
        >
          Ver todos ({lista.length})
        </Button>
      ) : null}

      <Dialog
        open={abierto}
        onClose={() => setAbierto(false)}
        title="Estado de tanques"
        description={`${lista.length} tanques de combustible`}
        size="lg"
      >
        <ul className="space-y-4">
          {lista.map((tanque) => (
            <TankRow key={tanque.id_tank} tanque={tanque} />
          ))}
        </ul>
      </Dialog>
    </div>
  );
}
